import axiosInstance from '@/libs/axios'
import type { BotPlanRequest, BotPlanResponse } from '@/types/pvpBotPlan'
import {
  PvpDeck,
  PvpMilestonesPayload,
  PvpLeaderboardPayload,
  PvpLoadout,
  PvpLoadoutSlotItem,
  PvpLoadoutSlotType,
  PvpMatchHistoryFilter,
  PvpMatchHistoryPayload,
  PvpMyLeaguePayload,
  PvpMyLeagueRange,
  PvpOnboardingStatus,
  PvpPlacementsPayload,
  SavedStrategy,
} from '@/stores/pvpLobbyStore'

// PVP base URL is resolved per-call, not at module load, because two local
// flows can be active in the same dev build:
//
//   1. `?pvpPlayer=1|2` (or `?qaAgent=...`, `?mock=1`) — the two-player /
//      QA mock simulation. Everything (matchmaking, decks, ready, etc.) is
//      served by the local mock server (`pvp-local-server/index.js` on
//      :3002). NEXT_PUBLIC_FREEDOM_API_URL is what the FE should hit.
//
//   2. No mock query flag — talk to the real NestJS BE on
//      NEXT_PUBLIC_PVP_LOCAL_API_URL (defaults to :3010), which uses the
//      Dockerised MySQL/Redis/Centrifugo. NEXT_PUBLIC_FREEDOM_API_URL still
//      points at the mock server because the real BE doesn't expose
//      cards/avatar/segment endpoints.
//
// Module-loading the URL picked the wrong host for the mock-flag flow once
// NEXT_PUBLIC_PVP_LOCAL_API_URL=http://localhost:3010 was added.
const isMockFlowUrl = (): boolean => {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  return params.has('pvpPlayer') || params.has('qaAgent') || params.get('mock') === '1'
}

const getBase = (): string => {
  const root = isMockFlowUrl()
    ? process.env.NEXT_PUBLIC_FREEDOM_API_URL
    : process.env.NEXT_PUBLIC_PVP_LOCAL_API_URL || process.env.NEXT_PUBLIC_FREEDOM_API_URL
  return `${root}/api/freedom-map-game/pvp`
}

// The PVP gateway intermittently returns 503 on the deck/loadout reads while
// the map-game service is restarting/unhealthy. A single transient 5xx
// shouldn't hard-fail the whole lobby, so retry idempotent GETs with backoff.
const RETRYABLE_STATUS = new Set([429, 502, 503, 504])
const withRetry = async <T>(
  fn: () => Promise<T>,
  retries = 2,
  baseDelayMs = 400,
): Promise<T> => {
  let lastErr: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn()
    } catch (err) {
      lastErr = err
      const status = (err as { response?: { status?: number } })?.response?.status
      if (attempt === retries || !status || !RETRYABLE_STATUS.has(status)) throw err
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * 2 ** attempt))
    }
  }
  throw lastErr
}

// Matches the BE return shape from PvpMatchmakingService.getMatchmakingStatus():
// { inQueue: boolean; position?: number; activeMatchId?: string }
export interface PvpMatchStatus {
  inQueue: boolean
  position?: number
  activeMatchId?: string
}

// BE getMatch returns the raw PvpMatch entity — uses `id`, not `matchId`,
// has no `roomId` field (channelName is the Centrifuge channel), and includes
// selectionDeadlineAt once the BE opens the selection window.
export interface PvpMatchData {
  id: string
  channelName: string
  roomId?: string
  status: string
  player1FdvId: number
  player2FdvId: number | null
  createdAt: string
  selectionDeadlineAt?: string | null
}

// DEBUG-ONLY (PVP bot picker, remove before prod)
export interface DebugBotProfile {
  id: number
  profileId: string | null
  nickname: string
  level: number
  avatarImageUrl: string | null
  squareAvatarImageUrl: string | null
  pvpPoints: number
  flavor: string | null
}

// DEBUG-ONLY (PVP bot picker, remove before prod)
export interface DebugBotMatchResponse {
  matchId: string
  channelName: string
  // Mock server returns a distinct roomId (`pvp_room_<matchId>`) used for
  // the game-route USER_JOINED / USER_READY channel. Real BE leaves it
  // undefined — callers must fall back to channelName in that case.
  roomId?: string
  opponent: {
    fdvId: number
    displayName: string
    level: number
    avatarImageUrl: string | null
    squareAvatarImageUrl?: string | null
    hp: number
    totalHp: number
    pvpPoints: number
    winRate?: number
    isBot: true
  }
}

export interface PvpCardSelectionEntry {
  cardAssetId: string
  quantity: number
}

export interface PvpGearInventoryItem {
  gamificationAssetId: string
  tokenId: string
  contractAddress: string
  name: string
  imageUrl: string
  rarity?: string
  isEquipped: boolean
}

export interface PvpGearInventoryResponse {
  slots: Record<PvpLoadoutSlotType, { items: PvpGearInventoryItem[] }>
  total: number
}

export interface SaveDeckPayload {
  name: string
  cards: { cardAssetId: string; quantity: number }[]
}

export interface SaveLoadoutPayload {
  name: string
  slots: PvpLoadoutSlotItem[]
}

export interface SelectDeckLoadoutPayload {
  deckId: number
  loadoutId?: number
}

export interface PvpResultResponse {
  newPvpPoints: number
  pointsChange: number
  newWinRate: number
}

// Rich PvP result payload that backs scape-pvp-UI/screens/s-35 (win) and
// s-36 (lose). Mirrors the BE PvpResultPayloadDto.
export interface PvpResultPayload {
  matchId: string
  isWin: boolean
  isBot: boolean
  apChange: number
  ap: number
  ratingChange: number
  ratingBefore: number
  ratingAfter: number
  tier: PvpTier
  division: number
  divisionAp: number
  divisionApBefore: number
  divisionApMax: number
  duration: number
  totalGames: number
  winStreak: number
  bestWinStreak: number
  lossStreak: number
  totalWins: number
  totalLosses: number
  tierChangedTo: { tier: PvpTier; division: number } | null
}

export interface PvpResultEnvelope {
  status: 'finalized' | 'pending'
  result?: PvpResultPayload
}

export type PvpTier = 'recruit' | 'challenger' | 'warlord' | 'champion' | 'legend'

export interface PvpStats {
  fdvId?: number
  pvpPoints: number
  totalWins: number
  totalLosses: number
  winRate: number
  winStreak: number
  bestWinStreak: number
  rating: number
  tier: PvpTier
  division: number
  divisionAp: number
  seasonalApEarned: number
  ap: number
  /** Live global rank across all tiers (1-based); null when not yet ranked. */
  rank?: number | null
  totalGames: number
  placementGamesPlayed: number
  demotionShieldLosses: number
  inDemotionMatch: number
  currentSeasonId: number | null
  // BE surfaces these from the player_stats row when a soft reset is pending
  // — i.e. between season-end and the player completing 5 placements in the
  // new season. Used by the s-12 banner ("You'll start at {placeholder}…").
  pendingResetTier?: PvpTier | null
  pendingResetDivision?: number | null
}

export interface PvpLeaderboardEntry {
  rank: number
  fdvId: number
  rating: number
  tier: PvpTier
  division: number
  divisionAp: number
  totalWins: number
  totalLosses: number
  winRate: number
}

export interface PvpRecentMatch {
  matchId: string
  result: 'win' | 'loss' | 'draw'
  pointsDelta: number
  isBot: boolean
  finishedAt: string | null
}

export interface PvpSeasonPhase {
  phase: 'active' | 'off_season' | 'none'
  season?: {
    id: number
    name: string
    seasonNumber: number
    status: 'active' | 'off_season' | 'archived'
    startsAt: string
    endsAt: string
    offSeasonEndsAt: string
  }
}

// --- Server-authoritative mid-game state DTOs ---
// One side of a snapshot. Sent in the submitter's OWN perspective (self/opponent
// labels, not raw seat indices); the BE composes GET in the requester's view.
export interface PvpStateSide {
  fdvId: number | string | null
  hp: number
  totalHp: number
  shield: number
  hasShield: boolean
  effects: unknown[]
  handCount: number
  dead: boolean
  turnSkipper: number
}

export interface PvpStateSnapshot {
  seq: number
  submittedByFdvId: number | string | null
  turnCharIndex: number
  pvpActiveUserId: string | null
  turnStartAt: number | null
  self: PvpStateSide
  opponent: PvpStateSide
  deckRemaining?: number
}

export interface PvpStateResponse {
  exists: boolean
  seq?: number
  turnCharIndex?: number
  pvpActiveUserId?: string | null
  turnStartAt?: number | null
  self?: PvpStateSide
  opponent?: PvpStateSide
  ageMs?: number
}

export const pvpService = {
  enterQueue: async (): Promise<void> => {
    const url = `${getBase()}/matchmaking/join`
    try {
      await axiosInstance.post(url)
    } catch (error) {
      throw error
    }
  },

  cancelQueue: async (): Promise<void> => {
    const url = `${getBase()}/matchmaking/cancel`
    try {
      await axiosInstance.post(url)
    } catch (error) {
      throw error
    }
  },

  getMatchStatus: async (): Promise<PvpMatchStatus> => {
    const url = `${getBase()}/matchmaking/status`
    try {
      const response = await axiosInstance.get<PvpMatchStatus>(url)
      return response.data
    } catch (error) {
      throw error
    }
  },

  // DEBUG-ONLY (PVP bot picker, remove before prod)
  // Lists active bot profiles for the staging-only manual bot-test picker.
  // Returns 404 from BE unless PVP_DEBUG_ENABLED=true.
  listDebugBots: async (): Promise<DebugBotProfile[]> => {
    const url = `${getBase()}/debug/bots`
    const response = await axiosInstance.get<DebugBotProfile[]>(url)
    return response.data
  },

  // DEBUG-ONLY (PVP bot picker, remove before prod)
  // Bypass matchmaking queue and immediately create a match against a
  // specific bot. Forfeits any active match for the player first (BE side).
  startDebugBotMatch: async (botProfileId: number): Promise<DebugBotMatchResponse> => {
    const url = `${getBase()}/debug/match-bot`
    const response = await axiosInstance.post<DebugBotMatchResponse>(url, {
      botProfileId,
    })
    return response.data
  },

  getMatch: async (matchId: string): Promise<PvpMatchData> => {
    const url = `${getBase()}/match/${matchId}`
    try {
      const response = await axiosInstance.get<PvpMatchData>(url)
      return response.data
    } catch (error) {
      throw error
    }
  },

  submitCardSelection: async (matchId: string, cards: PvpCardSelectionEntry[]): Promise<void> => {
    const url = `${getBase()}/match/${matchId}/select-cards`
    try {
      await axiosInstance.post(url, { cards })
    } catch (error) {
      throw error
    }
  },

  submitMatchSelection: async (matchId: string, payload: SelectDeckLoadoutPayload): Promise<void> => {
    const url = `${getBase()}/match/${matchId}/select-cards`
    try {
      await axiosInstance.post(url, payload)
    } catch (error) {
      throw error
    }
  },

  signalReady: async (matchId: string): Promise<{ bothReady: boolean }> => {
    const url = `${getBase()}/match/${matchId}/ready`
    try {
      const res = await axiosInstance.post<{ bothReady: boolean }>(url)
      return { bothReady: !!res?.data?.bothReady }
    } catch (error) {
      throw error
    }
  },

  submitPvpResult: async (
    matchId: string,
    result: 'win' | 'lose' | 'draw',
    duration: number,
  ): Promise<PvpResultResponse> => {
    const url = `${getBase()}/match/${matchId}/result`
    try {
      const response = await axiosInstance.post<PvpResultResponse>(url, { result, duration })
      return response.data
    } catch (error) {
      throw error
    }
  },

  // Matches the actual BE contract (PvpMatchResultDto): { gameResult, duration }
  // where gameResult is the PvpGameResult enum (Win | Lose | Abandon, capitalised).
  // Used by the resume-or-abandon flow on lobby mount; can replace
  // `submitPvpResult` later once all callers migrate.
  submitMatchResult: async (
    matchId: string,
    payload: { gameResult: 'Win' | 'Lose' | 'Abandon'; duration: number },
  ): Promise<{ status: string }> => {
    const url = `${getBase()}/match/${matchId}/result`
    const response = await axiosInstance.post<{ status: string }>(url, payload)
    return response.data
  },

  // PvP result screen feed (scape-pvp-UI/screens/s-35 win, s-36 lose).
  // POST submits the result and, when finalized for the caller, returns a
  // rich payload (AP/ELO deltas, tier+division before/after, streaks,
  // duration). status:'pending' fires only for the player who submitted
  // first in a real-PvP match — they should call `getPvpResultPayload`
  // afterwards (or wait for the pvp:matchResult socket event).
  submitPvpResultPayload: async (
    matchId: string,
    payload: { gameResult: 'Win' | 'Lose' | 'Abandon'; duration: number },
  ): Promise<PvpResultEnvelope> => {
    const url = `${getBase()}/match/${matchId}/pvp-result`
    const response = await axiosInstance.post<PvpResultEnvelope>(url, payload)
    return response.data
  },

  getPvpResultPayload: async (matchId: string): Promise<PvpResultEnvelope> => {
    const url = `${getBase()}/match/${matchId}/pvp-result`
    const response = await axiosInstance.get<PvpResultEnvelope>(url)
    return response.data
  },

  // Resets the BE's 40s inactivity watchdog. Called every 15s by
  // usePvpHeartbeat while the player is on the game stage. Best-effort —
  // failures are non-fatal (the watchdog only matters when the user has
  // genuinely walked away, in which case the heartbeat can't reach the BE
  // anyway).
  heartbeat: async (matchId: string): Promise<void> => {
    const url = `${getBase()}/match/${matchId}/heartbeat`
    try {
      await axiosInstance.post(url)
    } catch {
      // ignore — see comment above
    }
  },

  // Server-authoritative mid-game state. Persist a snapshot after each resolved
  // action/turn (best-effort; a transient failure self-heals on the next turn
  // via a higher seq). The server keeps max(seq) per side and rejects stale.
  postMatchState: async (
    matchId: string,
    body: PvpStateSnapshot,
  ): Promise<{ accepted: boolean; seq: number; storedSeq?: number }> => {
    const url = `${getBase()}/match/${matchId}/state`
    try {
      const res = await axiosInstance.post<{ accepted: boolean; seq: number; storedSeq?: number }>(url, body)
      return res.data
    } catch {
      return { accepted: false, seq: body.seq }
    }
  },

  // Fetch the latest snapshot (composed for the requester's perspective) to
  // hydrate Zustand on reconnect / game-route mount. Returns {exists:false} if
  // there is no snapshot yet (pre-first-action) or on error.
  getMatchState: async (matchId: string): Promise<PvpStateResponse> => {
    const url = `${getBase()}/match/${matchId}/state`
    try {
      const res = await axiosInstance.get<PvpStateResponse>(url)
      return res.data
    } catch {
      return { exists: false }
    }
  },

  // Seed the BE bot's view of the human player's actual HP at match start.
  // The bot doesn't subscribe to Centrifugo, so INITIAL_STATE_SYNC is
  // invisible to it. Without this call state.opponentHp stays at 100
  // (HUMAN_DEFAULT_HP) and the +130ms authoritative override snaps the
  // player's HP bar to the wrong value after the bot's first action.
  notifyInitialState: async (matchId: string, hp: number, totalHp?: number): Promise<void> => {
    const url = `${getBase()}/match/${matchId}/initial-state`
    // eslint-disable-next-line no-console
    // console.log('[PVP bot] POST initial-state →', { matchId, hp, totalHp })
    try {
      const res = await axiosInstance.post(url, { hp, totalHp })
      // eslint-disable-next-line no-console
      // console.log('[PVP bot] POST initial-state ← OK', res.status, res.data)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] POST initial-state FAILED', err)
    }
  },

  // Tell the BE bot agent who won the coin toss. firstTurnIndex===1 means
  // the bot opens the match — without this, the bot sits idle waiting for
  // the human's first PLAYER_ACTION_SYNC that will never arrive.
  notifyCoinToss: async (matchId: string, firstTurnIndex: 4 | 1): Promise<void> => {
    const url = `${getBase()}/match/${matchId}/coin-toss`
    // eslint-disable-next-line no-console
    // console.log('[PVP bot] POST coin-toss →', { matchId, firstTurnIndex })
    try {
      const res = await axiosInstance.post(url, { firstTurnIndex })
      // eslint-disable-next-line no-console
      // console.log('[PVP bot] POST coin-toss ← OK', res.status, res.data)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] POST coin-toss FAILED', err)
    }
  },

  // Fetch the bot's full deck cardId list at match start. FE owns
  // shuffle + hand-draw + channel state from this point on (see
  // botStore.ts).
  fetchBotDeck: async (matchId: string): Promise<string[] | null> => {
    const url = `${getBase()}/match/${matchId}/bot-deck`
    // eslint-disable-next-line no-console
    // console.log('[PVP bot] GET bot-deck →', { matchId })
    try {
      const res = await axiosInstance.get<{ cardIds: string[] }>(url)
      // eslint-disable-next-line no-console
      // console.log('[PVP bot] GET bot-deck ← OK', res.status, res.data?.cardIds?.length, 'cards')
      return Array.isArray(res.data?.cardIds) ? res.data.cardIds : []
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] GET bot-deck FAILED', (err as any)?.message || err)
      return null
    }
  },

  // Fetch the bot's gear modifiers (0..1 fractions) at match start. Sourced
  // from the BE bot session (same gear the LLM prompt uses), so it's reliable
  // regardless of `pvp:matchFound` push timing (polling-fallback / resume
  // paths don't carry gear). FE converts ×100 → percent into botStore.botGear.
  fetchBotGear: async (
    matchId: string,
  ): Promise<{ damageIncrease: number; damageReduction: number; blockIncreaser: number; healIncrease: number } | null> => {
    const url = `${getBase()}/match/${matchId}/bot-gear`
    try {
      const res = await axiosInstance.get<{
        damageIncrease: number
        damageReduction: number
        blockIncreaser: number
        healIncrease: number
      }>(url)
      return res.data ?? null
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] GET bot-gear FAILED', (err as any)?.message || err)
      return null
    }
  },

  // FE-authoritative bot-plan endpoints. The FE owns all game state (hp,
  // shield, effects, turn order) and POSTs a snapshot of it to the BE on
  // every Action card play / skip / timer expiry. The BE returns an
  // ordered action list (channel → fast → action / or skip). The FE
  // executes the actions locally — no socket publish from the BE per
  // action. See freedom-api-map-game's planBotTurn/planBotFallback for
  // the BE side and BotPlanRequestDto / BotPlanResponseDto for the wire
  // format.
  //
  // 30s timeout on /bot-plan triggers the FE stall watchdog to escalate
  // to /bot-fallback-plan (heuristic-only, fast). If THAT also times out
  // (~10s), the bot loses its turn — FE shows a toast and continues the
  // match.
  requestBotPlan: async (
    matchId: string,
    snapshot: BotPlanRequest,
    opts?: { timeoutMs?: number; signal?: AbortSignal },
  ): Promise<BotPlanResponse | null> => {
    const url = `${getBase()}/match/${matchId}/bot-plan`
    const timeoutMs = opts?.timeoutMs ?? 30_000
    // eslint-disable-next-line no-console
    // console.log('[PVP bot] POST bot-plan →', { matchId, turnNumber: snapshot.turnNumber, isBotTurnStart: snapshot.isBotTurnStart })
    try {
      const res = await axiosInstance.post<BotPlanResponse>(url, snapshot, {
        timeout: timeoutMs,
        signal: opts?.signal,
      })
      // eslint-disable-next-line no-console
      // console.log('[PVP bot] POST bot-plan ← OK', res.status, res.data?.actions?.length, 'actions,', res.data?.source)
      return res.data
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] POST bot-plan FAILED', (err as any)?.message || err)
      return null
    }
  },

  requestBotFallbackPlan: async (
    matchId: string,
    snapshot: BotPlanRequest,
    opts?: { timeoutMs?: number; signal?: AbortSignal },
  ): Promise<BotPlanResponse | null> => {
    const url = `${getBase()}/match/${matchId}/bot-fallback-plan`
    const timeoutMs = opts?.timeoutMs ?? 10_000
    // eslint-disable-next-line no-console
    // console.log('[PVP bot] POST bot-fallback-plan →', { matchId })
    try {
      const res = await axiosInstance.post<BotPlanResponse>(url, snapshot, {
        timeout: timeoutMs,
        signal: opts?.signal,
      })
      // eslint-disable-next-line no-console
      // console.log('[PVP bot] POST bot-fallback-plan ← OK', res.status, res.data?.actions?.length, 'actions')
      return res.data
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] POST bot-fallback-plan FAILED', (err as any)?.message || err)
      return null
    }
  },

  // Freeze (paused=true) / re-arm (paused=false) the BE bot no-activity
  // watchdog while the ?botTest review overlay holds the game. Without this
  // the BE force-plays a heuristic bot turn after 30s of "no activity" — which
  // the FE applies as the bot playing new cards mid-review. Best-effort; the
  // FE driver still owns the turn, so a dropped ping only risks the original
  // 30s watchdog (now also guarded on the FE socket side).
  setBotReviewPause: async (matchId: string, paused: boolean): Promise<void> => {
    const url = `${getBase()}/match/${matchId}/bot-review-pause`
    try {
      await axiosInstance.post(url, { paused })
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[PVP bot] POST bot-review-pause FAILED', (err as any)?.message || err)
    }
  },

  getPvpStats: async (): Promise<PvpStats> => {
    const url = `${getBase()}/stats`
    try {
      const response = await axiosInstance.get<PvpStats>(url)
      return response.data
    } catch (error) {
      throw error
    }
  },

  getPublicPvpStats: async (fdvId: number): Promise<PvpStats> => {
    const url = `${getBase()}/stats/${fdvId}`
    const response = await axiosInstance.get<PvpStats>(url)
    return response.data
  },

  getBotPublicProfile: async (fakeFdvId: number): Promise<{
    fdvId: number
    displayName: string
    avatarImageUrl: string
    avatarImagePortraitUrl: string
    level: number
    rank: number
    currentXp: number
    xpProgressPercentage: number
    remainingXp: number
    avatarRarity: string
    badges: string[]
    maxHp: number
    currentHp: number
    winRate: number
    pvpPoints: number
    totalWins: number
    totalLosses: number
    totalGames: number
    winStreak: number
  }> => {
    const url = `${getBase()}/bots/profile/${fakeFdvId}`
    const response = await axiosInstance.get(url)
    return response.data
  },

  getLeaderboard: async (): Promise<PvpLeaderboardEntry[]> => {
    const url = `${getBase()}/leaderboard`
    const response = await axiosInstance.get<PvpLeaderboardEntry[]>(url)
    return response.data
  },

  getCurrentSeason: async (): Promise<PvpSeasonPhase> => {
    const url = `${getBase()}/season`
    const response = await axiosInstance.get<PvpSeasonPhase>(url)
    return response.data
  },

  // scape-pvp-UI/screens/s-12 — final-rank / peak-AP / rewards snapshot for
  // the most recently archived season. Returns null until the season-end
  // scheduler has run, so callers must tolerate `null`.
  getSeasonResult: async (): Promise<{
    seasonId: number
    seasonNumber: number
    finalRank: number
    finalTier: string
    finalDivision: number
    peakTotalAp: number
    totalGames: number
    rewards: Array<{ id: number; type: 'gear' | 'cosmetic'; name: string; claimed: boolean }>
  } | null> => {
    const url = `${getBase()}/season-result`
    const response = await axiosInstance.get(url)
    return response.data
  },

  getRecentMatches: async (limit = 10): Promise<PvpRecentMatch[]> => {
    const url = `${getBase()}/matches/recent`
    const response = await axiosInstance.get<PvpRecentMatch[]>(url, {
      params: { limit },
    })
    return response.data
  },

  getStrategies: async (): Promise<SavedStrategy[]> => {
    const url = `${getBase()}/strategy`
    try {
      const response = await axiosInstance.get<SavedStrategy[]>(url)
      return response.data
    } catch (error) {
      throw error
    }
  },

  saveStrategy: async (name: string, cards: PvpCardSelectionEntry[]): Promise<SavedStrategy> => {
    const url = `${getBase()}/strategy`
    try {
      const response = await axiosInstance.post<SavedStrategy>(url, { name, cards })
      return response.data
    } catch (error) {
      throw error
    }
  },

  deleteStrategy: async (strategyId: string): Promise<void> => {
    const url = `${getBase()}/strategy/${strategyId}`
    try {
      await axiosInstance.delete(url)
    } catch (error) {
      throw error
    }
  },

  // ─── Onboarding ────────────────────────────────────────────────────────────
  getOnboardingStatus: async (): Promise<PvpOnboardingStatus> => {
    const response = await axiosInstance.get<PvpOnboardingStatus>(`${getBase()}/onboarding/status`)
    return response.data
  },

  completeOnboarding: async (): Promise<{
    status: PvpOnboardingStatus
    loadout: PvpLoadout | null
  }> => {
    const response = await axiosInstance.post<{
      status: PvpOnboardingStatus
      loadout: PvpLoadout | null
    }>(`${getBase()}/onboarding/complete`)
    return response.data
  },

  // ─── Gears (wearable inventory) ────────────────────────────────────────────
  getGearInventory: async (excludeEquipped = false): Promise<PvpGearInventoryResponse> => {
    const response = await axiosInstance.get<PvpGearInventoryResponse>(`${getBase()}/gears/inventory`, {
      params: { excludeEquipped },
    })
    return response.data
  },

  // ─── Decks ─────────────────────────────────────────────────────────────────
  listDecks: async (): Promise<PvpDeck[]> => {
    const response = await withRetry(() =>
      axiosInstance.get<PvpDeck[]>(`${getBase()}/decks`),
    )
    return response.data
  },

  getDeck: async (id: number): Promise<PvpDeck> => {
    const response = await axiosInstance.get<PvpDeck>(`${getBase()}/decks/${id}`)
    return response.data
  },

  createDeck: async (payload: SaveDeckPayload): Promise<PvpDeck> => {
    const response = await axiosInstance.post<PvpDeck>(`${getBase()}/decks`, payload)
    return response.data
  },

  updateDeck: async (id: number, payload: SaveDeckPayload): Promise<PvpDeck> => {
    const response = await axiosInstance.put<PvpDeck>(`${getBase()}/decks/${id}`, payload)
    return response.data
  },

  deleteDeck: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${getBase()}/decks/${id}`)
  },

  // ─── Loadouts ──────────────────────────────────────────────────────────────
  listLoadouts: async (): Promise<PvpLoadout[]> => {
    const response = await withRetry(() =>
      axiosInstance.get<PvpLoadout[]>(`${getBase()}/loadouts`),
    )
    return response.data
  },

  getLoadout: async (id: number): Promise<PvpLoadout> => {
    const response = await axiosInstance.get<PvpLoadout>(`${getBase()}/loadouts/${id}`)
    return response.data
  },

  createLoadout: async (payload: SaveLoadoutPayload): Promise<PvpLoadout> => {
    const response = await axiosInstance.post<PvpLoadout>(`${getBase()}/loadouts`, payload)
    return response.data
  },

  autoGenerateLoadout: async (): Promise<PvpLoadout> => {
    const response = await axiosInstance.post<PvpLoadout>(`${getBase()}/loadouts/auto-generate`)
    return response.data
  },

  updateLoadout: async (id: number, payload: SaveLoadoutPayload): Promise<PvpLoadout> => {
    const response = await axiosInstance.put<PvpLoadout>(`${getBase()}/loadouts/${id}`, payload)
    return response.data
  },

  deleteLoadout: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${getBase()}/loadouts/${id}`)
  },

  // ─── Ranked pages (Phase E) ────────────────────────────────────────────────
  getLeaderboardTop100: async (
    scope: 'mine' | 'global' = 'mine',
    tier?: PvpTier,
    range?: PvpMyLeagueRange,
  ): Promise<PvpLeaderboardPayload> => {
    // range → division number for the BE filter. 'III'=1, 'II'=2, 'I'=3.
    // 'all' (or undefined) sends no division so every division shows.
    const RANGE_TO_DIVISION: Record<PvpMyLeagueRange, number | undefined> = {
      III: 1,
      II: 2,
      I: 3,
      all: undefined,
    }
    const division = range ? RANGE_TO_DIVISION[range] : undefined
    const response = await axiosInstance.get<PvpLeaderboardPayload>(`${getBase()}/leaderboard/top100`, {
      params: {
        scope,
        ...(tier ? { tier } : {}),
        ...(division != null ? { division } : {}),
      },
    })
    return response.data
  },

  getMyLeague: async (range: PvpMyLeagueRange = 'II'): Promise<PvpMyLeaguePayload> => {
    const response = await axiosInstance.get<PvpMyLeaguePayload>(`${getBase()}/league/me`, {
      params: { range },
    })
    return response.data
  },

  getPlacementsStatus: async (): Promise<PvpPlacementsPayload> => {
    // BE returns a sparse results array (only played matches) and names the
    // count field `matchesRequired`; the FE store expects a fixed-length
    // 5-slot array and the name `totalMatches`. Adapt here so the component
    // can render the mock s-06 grid without ever seeing fewer than 5 tiles.
    // Unplayed slots carry `result: null` so the view's "next vs locked"
    // logic (first null = next, rest locked) continues to work.
    const response = await axiosInstance.get<{
      seasonNumber?: number
      dayInSeason?: number
      matchesPlayed: number
      matchesRequired: number
      isComplete: boolean
      results: Array<{ index: number; result: 'win' | 'loss' | 'draw' }>
      projectedTier: PvpPlacementsPayload['projectedTier']
      projectedDivision: number
      projectedMmr: number
      demotionShieldActive: boolean
      shieldLossesRemaining: number
    }>(`${getBase()}/placements/status`)
    const raw = response.data
    const total = raw.matchesRequired
    const filled: PvpPlacementsPayload['results'] = Array.from({ length: total }, (_, i) => {
      const played = raw.results.find((r) => r.index === i + 1)
      return played ? { index: i + 1, result: played.result } : { index: i + 1, result: null }
    })
    return {
      seasonNumber: raw.seasonNumber ?? 0,
      dayInSeason: raw.dayInSeason ?? 0,
      matchesPlayed: raw.matchesPlayed,
      totalMatches: total,
      results: filled,
      projectedTier: raw.projectedTier,
      projectedDivision: raw.projectedDivision,
      projectedMmr: raw.projectedMmr,
      demotionShieldActive: raw.demotionShieldActive,
      shieldLossesRemaining: raw.shieldLossesRemaining,
    }
  },

  getMilestones: async (): Promise<PvpMilestonesPayload> => {
    const response = await axiosInstance.get<PvpMilestonesPayload>(
      `${getBase()}/milestones`,
    )
    const raw = response.data
    return {
      ap: raw?.ap ?? 0,
      milestones: raw?.milestones ?? [],
    }
  },

  claimMilestone: async (level: number, choice?: string): Promise<void> => {
    await axiosInstance.post(
      `${getBase()}/milestones/${level}/claim`,
      choice ? { choice } : {},
    )
  },

  getMatchHistory: async (
    filter: PvpMatchHistoryFilter = 'all',
    cursor?: string | null,
    limit = 20,
  ): Promise<PvpMatchHistoryPayload & { nextCursor: string | null }> => {
    // BE returns {overview, counts, rows, nextCursor} but the FE store/view
    // expects `items`. Rename here so the view's filter memo + Bot tab work.
    const response = await axiosInstance.get<{
      overview: PvpMatchHistoryPayload['overview'] & { seasonNumber: number | null }
      counts: PvpMatchHistoryPayload['counts']
      rows: PvpMatchHistoryPayload['items']
      nextCursor: string | null
    }>(`${getBase()}/match-history`, {
      params: {
        filter,
        ...(cursor ? { cursor } : {}),
        limit,
      },
    })
    const raw = response.data
    return {
      overview: { ...raw.overview, seasonNumber: raw.overview.seasonNumber ?? 0 },
      counts: raw.counts,
      items: raw.rows ?? [],
      nextCursor: raw.nextCursor,
    }
  },
}
